import { Stack, Text } from '@careflux/primitives';
import { useState } from 'react';
import { AudioQualityFilterBar } from './AudioQualityFilterBar';
import { AudioQualityOverview, formatAudioQualityScore } from './AudioQualityOverview';
import { AudioQualityTimelineTable } from './AudioQualityTimelineTable';
import { QueryState } from './QueryState';
import { TrendChart } from './TrendChart';
import type { AudioQualityTabProps } from './audioQuality.types';
import { useAudioQualityAnalytics, useDebouncedFilters } from './useSummaryQualityQueries';

export const AudioQualityTab = ({ filters, bucket, onBucketChange, onFilterChange }: AudioQualityTabProps) => {
  const [scorerVersion, setScorerVersion] = useState<string | null>(null);
  const debouncedFilters = useDebouncedFilters(filters);
  const query = useAudioQualityAnalytics(debouncedFilters, bucket, scorerVersion);
  const data = query.data;
  return (
    <Stack gap="lg">
      <AudioQualityFilterBar
        filters={filters}
        orgOptions={data?.orgOptions ?? []}
        scorerVersions={data?.scorerVersions ?? []}
        scorerVersion={scorerVersion}
        onFilterChange={onFilterChange}
        onScorerVersionChange={setScorerVersion}
      />
      <Text size="sm" color="muted">
        Aðeins upptökur sem söfnun hljóðgæða náði til eru taldar hér, ekki allar eldri upptökur. Ómetnar upptökur
        teljast ekki með í meðaltali eða hlutfalli slakra gæða.
      </Text>
      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        loadingLabel="Sæki hljóðgæði"
        errorTitle="Hljóðgæði hlóðust ekki"
      />
      {data ? (
        <>
          <AudioQualityOverview data={data} />
          <TrendChart
            title="Hljóðgæði yfir tíma"
            description="Áætlaður skýrleiki tals, ekki nákvæmni umritunar"
            points={data.timeline}
            series={[{ dataKey: 'scoreMean', label: 'Hljóðgæði', color: 'hsl(var(--chart-1))' }]}
            formatValue={formatAudioQualityScore}
            yAxisDomain={[0, 5]}
            bucket={bucket}
            onBucketChange={onBucketChange}
            footnote="Meðaltal og hlutfall slakra gæða birtast aðeins fyrir að minnsta kosti 10 metnar upptökur í hverju tímabili."
          />
          <AudioQualityTimelineTable points={data.timeline} />
        </>
      ) : null}
    </Stack>
  );
};
